import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL
} from 'firebase/storage';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, Divider, Tooltip, Switch, FormControlLabel, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { canUseFlatbox } from '../utils/permissions';
import { isFlatboxProduct } from '../modules/crm/flatboxLead';

const CURRENCIES = ['SEK', 'USD', 'EUR', 'NOK', 'DKK'];
const PRICE_MODELS = [
  'Engångslicens', 'Prenumeration/år', 'Prenumeration/mån',
  'Per användare', 'Per projekt', 'Offert', 'Gratis'
];

const EMPTY_FORM = {
  name: '', shortDescription: '', description: '',
  price: '', currency: 'SEK', priceModel: '',
  website: '', imageUrl: '', category: '',
  active: true,
};

export default function Products() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  // Produkter med egen konfigurator i LagbasN
  const configuratorPath = product =>
    isFlatboxProduct(product) && canUseFlatbox(userProfile) ? '/flatbox' : null;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef();

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'products'), orderBy('name')));
    setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  function openNew() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setUploadError('');
    setDialog(true);
  }

  function openEdit(product) {
    setForm({ ...EMPTY_FORM, ...product });
    setEditingId(product.id);
    setUploadError('');
    setDialog(true);
  }

  async function handleImageUpload(file) {
    if (!file) return;
    const maxMB = 5;
    if (file.size > maxMB * 1024 * 1024) {
      setUploadError(`Bilden är för stor (max ${maxMB} MB)`);
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const ext = file.name.split('.').pop();
      const path = `products/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      setForm(f => ({ ...f, imageUrl: url }));
    } catch (e) {
      setUploadError('Kunde inte ladda upp bilden. Försök igen.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    const data = {
      ...form,
      price: form.price ? Number(form.price) : null,
      updatedAt: new Date(),
    };
    if (editingId) {
      await updateDoc(doc(db, 'products', editingId), data);
    } else {
      await addDoc(collection(db, 'products'), {
        ...data,
        createdBy: currentUser.uid,
        createdAt: new Date(),
      });
    }
    setSaving(false);
    setDialog(false);
    load();
  }

  async function handleDelete(product) {
    if (!window.confirm(`Ta bort "${product.name}"? Åtgärden kan inte ångras.`)) return;
    await deleteDoc(doc(db, 'products', product.id));
    setProducts(prev => prev.filter(p => p.id !== product.id));
  }

  async function handleToggleActive(product) {
    await updateDoc(doc(db, 'products', product.id), { active: !product.active });
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, active: !p.active } : p));
  }

  function formatPrice(product) {
    if (!product.price && !product.priceModel) return null;
    if (!product.price) return product.priceModel;
    const priceStr = Number(product.price).toLocaleString('sv-SE');
    return `${priceStr} ${product.currency || ''}${product.priceModel ? ' · ' + product.priceModel : ''}`;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Affärer – Produktkatalog</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {products.length} produkter
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
          Ny produkt
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <StorefrontIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Inga produkter ännu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Lägg till era produkter här — de visas som valbara i leads-skapandet.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              Skapa första produkten
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2.5}>
            {products.map(product => (
              <Grid item xs={12} sm={6} lg={4} key={product.id}>
                <Paper sx={{
                  height: '100%', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                  opacity: product.active ? 1 : 0.55,
                  transition: 'all 0.15s',
                  '&:hover': { boxShadow: 4, '& .product-actions': { opacity: 1 } }
                }}>
                  {/* Product image */}
                  <Box sx={{ position: 'relative', height: 160, bgcolor: '#f0f0f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden' }}>
                    {product.imageUrl ? (
                      <Box component="img" src={product.imageUrl} alt={product.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }} />
                    ) : (
                      <ImageIcon sx={{ fontSize: 64, color: '#ccc' }} />
                    )}
                    {!product.active && (
                      <Chip label="Inaktiv" size="small"
                        sx={{ position: 'absolute', top: 8, left: 8,
                          bgcolor: '#795548', color: 'white', fontSize: '0.65rem' }} />
                    )}
                    {product.category && (
                      <Chip label={product.category} size="small"
                        sx={{ position: 'absolute', top: 8, right: 8,
                          bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '0.65rem' }} />
                    )}
                    <Box className="product-actions"
                      sx={{ position: 'absolute', bottom: 8, right: 8,
                        opacity: 0, transition: 'opacity 0.15s',
                        display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => openEdit(product)}
                        sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(product)}
                        sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#ffebee', color: 'error.main' } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
                      {product.name}
                    </Typography>
                    {product.shortDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {product.shortDescription}
                      </Typography>
                    )}
                    {formatPrice(product) && (
                      <Typography variant="body2" fontWeight={600}
                        sx={{ color: '#FF6B00', mb: 1 }}>
                        {formatPrice(product)}
                      </Typography>
                    )}
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1, alignItems: 'center',
                      justifyContent: 'space-between', pt: 1 }}>
                      {product.website ? (
                        <Button size="small" startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(
                            product.website.startsWith('http') ? product.website : 'https://' + product.website,
                            '_blank'
                          )}
                          sx={{ fontSize: '0.72rem', color: '#1565C0' }}>
                          Webbsida
                        </Button>
                      ) : <span />}
                      {configuratorPath(product) && (
                        <Button size="small" variant="contained" startIcon={<RequestQuoteIcon />}
                          onClick={() => navigate(configuratorPath(product))}
                          sx={{ fontSize: '0.72rem', bgcolor: '#E30613', '&:hover': { bgcolor: '#B8050F' } }}>
                          Konfigurator
                        </Button>
                      )}
                      <Tooltip title={product.active ? 'Klicka för att inaktivera' : 'Klicka för att aktivera'}>
                        <Chip
                          label={product.active ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          onClick={() => handleToggleActive(product)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: product.active ? '#E8F5E9' : '#f5f5f5',
                            color: product.active ? '#2E7D32' : 'text.secondary',
                            fontWeight: 600, fontSize: '0.65rem'
                          }} />
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Product dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon sx={{ color: '#FF6B00' }} />
            <Typography fontWeight="bold">
              {editingId ? 'Redigera produkt' : 'Ny produkt'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>

            {/* Image upload */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 100, height: 100, borderRadius: 2, overflow: 'hidden',
                  border: '2px dashed', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#f9f9f9', flexShrink: 0, cursor: 'pointer'
                }}
                  onClick={() => fileInputRef.current?.click()}>
                  {form.imageUrl ? (
                    <Box component="img" src={form.imageUrl} alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 36, color: '#ccc' }} />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => handleImageUpload(e.target.files[0])}
                  />
                  <Button variant="outlined" size="small" startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{ mb: 1 }}>
                    {uploading ? 'Laddar upp...' : 'Ladda upp bild'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block">
                    PNG, JPG, SVG · max 5 MB
                  </Typography>
                  <TextField fullWidth size="small" label="Eller klistra in bild-URL"
                    value={form.imageUrl} sx={{ mt: 1 }}
                    onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} />
                </Box>
              </Box>
              {uploadError && (
                <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                  {uploadError}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Produktnamn *" size="small"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Kort beskrivning" size="small"
                placeholder="Visas på produktkortet"
                value={form.shortDescription}
                onChange={e => setForm(f => ({ ...f, shortDescription: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Fullständig beskrivning" size="small"
                multiline rows={3}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
            <Grid item xs={5}>
              <TextField fullWidth label="Pris" size="small" type="number"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            </Grid>
            <Grid item xs={3}>
              <TextField select fullWidth label="Valuta" size="small"
                value={form.currency}
                onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField select fullWidth label="Prismodell" size="small"
                value={form.priceModel}
                onChange={e => setForm(f => ({ ...f, priceModel: e.target.value }))}>
                <MenuItem value="">–</MenuItem>
                {PRICE_MODELS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Kategori" size="small"
                placeholder="t.ex. Byggprojekt, SaaS, IoT"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Webbsida" size="small"
                placeholder="https://..."
                value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                    color="success" />
                }
                label={<Typography variant="body2">Aktiv (visas i lead-formulär)</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || uploading || !form.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Sparar...' : 'Spara produkt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
